/**
 * Catalog of shipped role-contextual defaults for the Experience Audit settings page.
 * Build Contract §1D — nav profiles, dashboard profiles, and composed home configs.
 */

import type { HomeDashboardConfig, HomeWidgetId } from '../types/prioritization/homeDashboard';
import { DEFAULT_HOME_SEARCH } from '../types/prioritization/homeDashboard';
import {
  DEFAULT_JOB_ROLES,
  JOB_FAMILIES,
  type JobFamilyId,
} from '../types/prioritization/jobRoles';
import { composeHomeDashboard } from './composeHomeDashboard';
import {
  getContributorNavProfileForJobSlug,
  getWorkspaceMemberNavProfile,
  type ContributorNavProfile,
} from './contributorNavProfiles';
import {
  getAdminShellDashboardProfile,
  getFamilyBaselineProfile,
  getRoleDashboardProfile,
  getRoleDashboardProfileSource,
  type RoleDashboardProfile,
  type RoleDashboardProfileSource,
} from './roleDashboardProfiles';

export type ExperienceAuditEntryKind = 'system-default' | 'job-role';

export interface ExperienceAuditEntry {
  id: string;
  kind: ExperienceAuditEntryKind;
  title: string;
  description?: string;
  jobSlug?: string;
  familyId?: JobFamilyId;
  familyLabel?: string;
  profileSource: RoleDashboardProfileSource | 'admin-shell';
  nav: ContributorNavProfile | null;
  dashboardProfile: RoleDashboardProfile;
  composedDashboard: HomeDashboardConfig;
}

export interface FamilyBaselineEntry {
  familyId: JobFamilyId;
  label: string;
  description: string;
  dashboardProfile: RoleDashboardProfile;
}

export interface ExperienceAuditCatalog {
  systemDefaults: ExperienceAuditEntry[];
  familyBaselines: FamilyBaselineEntry[];
  jobRoles: ExperienceAuditEntry[];
}

function jobRoleEntry(slug: string): ExperienceAuditEntry {
  const role = DEFAULT_JOB_ROLES.find((r) => r.slug === slug)!;
  return {
    id: slug,
    kind: 'job-role',
    title: role.displayName,
    description: role.description,
    jobSlug: slug,
    familyId: role.family,
    familyLabel: JOB_FAMILIES[role.family].label,
    profileSource: getRoleDashboardProfileSource(slug),
    nav: getContributorNavProfileForJobSlug(slug),
    dashboardProfile: getRoleDashboardProfile(slug),
    composedDashboard: composeHomeDashboard({
      assignedRoleNames: [slug],
      featuredJobRoleSlugs: [slug],
    }),
  };
}

export function buildExperienceAuditCatalog(): ExperienceAuditCatalog {
  const systemDefaults: ExperienceAuditEntry[] = [
    {
      id: 'admin-shell',
      kind: 'system-default',
      title: 'Admin shell user',
      description:
        'Workspace or platform admin without job-role assignments — full configuration sidebar and enterprise dashboard default.',
      profileSource: 'admin-shell',
      nav: null,
      dashboardProfile: getAdminShellDashboardProfile(),
      composedDashboard: composeHomeDashboard({
        assignedRoleNames: [],
        useAdminShellDefault: true,
      }),
    },
    {
      id: 'workspace-member-contributor',
      kind: 'system-default',
      title: 'Workspace member (contributor, no job role)',
      description:
        'User with only the workspace-member role — contributor shell baseline nav. With no job slugs resolved, the home dashboard uses the Session Participant emphasis.',
      profileSource: 'workspace-member-baseline',
      nav: getWorkspaceMemberNavProfile(),
      dashboardProfile: getRoleDashboardProfile('session-participant'),
      composedDashboard: composeHomeDashboard({
        assignedRoleNames: ['workspace-member'],
        useAdminShellDefault: false,
      }),
    },
  ];

  const familyBaselines: FamilyBaselineEntry[] = (
    Object.keys(JOB_FAMILIES) as JobFamilyId[]
  ).map((familyId) => ({
    familyId,
    label: JOB_FAMILIES[familyId].label,
    description: JOB_FAMILIES[familyId].description,
    dashboardProfile: getFamilyBaselineProfile(familyId),
  }));

  const jobRoles = DEFAULT_JOB_ROLES.map((role) => jobRoleEntry(role.slug));

  return { systemDefaults, familyBaselines, jobRoles };
}

export const HOME_WIDGET_DISPLAY_LABELS: Record<HomeWidgetId, string> = {
  getting_started: 'Quick Actions',
  getting_started_checklist: 'Getting Started Checklist',
  how_it_works: 'How Prioritization Works',
  prioritization_toolkit: 'Prioritization Toolkit',
  apps: 'Apps',
  active_cycles: 'Active Cycles',
  active_tracks: 'Active Tracks',
  my_responsibilities: 'My Tasks',
  items_requiring_attention: 'Items Requiring Attention',
  quick_actions: 'Quick Actions',
  recent_publications: 'Recent Publications',
  recent_activity: 'Recent Activity',
  intake_queue: 'Intake Queue',
  scoring_gaps: 'Scoring Gaps',
  readiness_blockers: 'Readiness Blockers',
  unresolved_dependencies: 'Unresolved Dependencies',
  upcoming_sessions: 'Upcoming Sessions',
  cycle_readiness: 'Cycle Readiness',
  ball_position: 'Ball Position (Catchball)',
  proposals_awaiting_review: 'Proposals Awaiting Review',
  objectives_progress: 'Objectives',
  plans_of_record: 'Plans of Record',
  watchlist: 'Watchlist',
  group_decisions: 'Group Decisions',
  stage_progress: 'Prioritization Progress',
  tips: 'Tips',
  best_practices: 'Best Practices',
  feature_spotlight: 'Feature Spotlight',
  saved_searches: 'My Saved Searches',
};

export const PROFILE_SOURCE_LABELS: Record<ExperienceAuditEntry['profileSource'], string> = {
  'role-override': 'Role-specific override',
  'family-baseline': 'Job family baseline',
  'workspace-member-baseline': 'Workspace member baseline',
  'admin-shell': 'Admin shell default',
};

export const EXPERIENCE_PREVIEW_QUERY_PARAM = 'experiencePreview';

const FAMILY_PREVIEW_PREFIX = 'family:';

export function experiencePreviewIdForFamily(familyId: JobFamilyId): string {
  return `${FAMILY_PREVIEW_PREFIX}${familyId}`;
}

export function getExperiencePreviewHomeHref(auditId: string): string {
  return `/prioritization/dashboard?${EXPERIENCE_PREVIEW_QUERY_PARAM}=${encodeURIComponent(auditId)}`;
}

function profileToHomeConfig(profile: RoleDashboardProfile): HomeDashboardConfig {
  return {
    layout: profile.layout ?? 'two-column',
    widgetIds: [...profile.widgetIds],
    widgetColumns: profile.widgetColumns,
    widgetHideIfEmpty: profile.widgetHideIfEmpty,
    customWidgets: [],
    search: profile.search ? { ...DEFAULT_HOME_SEARCH, ...profile.search } : { ...DEFAULT_HOME_SEARCH },
    updatedAt: new Date().toISOString(),
  };
}

let _previewLookup: Map<string, { title: string; config: HomeDashboardConfig }> | null = null;

function buildPreviewLookup(): Map<string, { title: string; config: HomeDashboardConfig }> {
  const catalog = buildExperienceAuditCatalog();
  const map = new Map<string, { title: string; config: HomeDashboardConfig }>();

  for (const entry of catalog.systemDefaults) {
    map.set(entry.id, { title: entry.title, config: entry.composedDashboard });
  }
  for (const entry of catalog.jobRoles) {
    map.set(entry.id, { title: entry.title, config: entry.composedDashboard });
  }
  for (const baseline of catalog.familyBaselines) {
    map.set(experiencePreviewIdForFamily(baseline.familyId), {
      title: `${baseline.label} family baseline`,
      config: profileToHomeConfig(baseline.dashboardProfile),
    });
  }

  return map;
}

/** Resolve an Experience Audit id to the home dashboard config admins can preview. */
export function resolveExperiencePreview(
  auditId: string,
): { title: string; config: HomeDashboardConfig } | null {
  if (!_previewLookup) _previewLookup = buildPreviewLookup();
  return _previewLookup.get(auditId) ?? null;
}
